import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { fetchPosts } from '../lib/queries.ts';
import type { Post } from '../lib/types.ts';

const STATUS_FILTERS = ['all', 'scheduled', 'publishing', 'published', 'failed'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

function statusColor(status: Post['status']): string {
  switch (status) {
    case 'published':
      return 'var(--money, #4ADE80)';
    case 'scheduled':
      return 'var(--cool, #38BDF8)';
    case 'publishing':
      return 'var(--amber, #F59E0B)';
    case 'failed':
      return 'var(--hot, #FF3D6E)';
    default:
      return 'var(--text-3, #6B7283)';
  }
}

function fmtDateTime(iso: string | null, locale: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PostsPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const locale = i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US';

  const postsQuery = useQuery({
    queryKey: ['posts', 'list', statusFilter],
    queryFn: () =>
      fetchPosts({
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        limit: 100,
      }),
    refetchInterval: (query) => {
      const data = query.state.data?.posts ?? [];
      const anyInFlight = data.some(
        (p) => p.status === 'scheduled' || p.status === 'publishing',
      );
      return anyInFlight ? 10_000 : 60_000;
    },
  });

  const posts = postsQuery.data?.posts ?? [];

  const counts = useMemo(() => {
    const acc: Record<string, number> = {
      all: posts.length,
      scheduled: 0,
      publishing: 0,
      published: 0,
      failed: 0,
    };
    for (const p of posts) acc[p.status] = (acc[p.status] ?? 0) + 1;
    return acc;
  }, [posts]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('posts.title')}</h1>
          <div className="sub">
            {t('posts.subtitle', { count: posts.length })}
          </div>
        </div>
        <div className="filters">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                background: statusFilter === s ? 'var(--neon-soft)' : 'transparent',
                color: statusFilter === s ? 'var(--neon)' : 'var(--text-2)',
                borderColor: statusFilter === s ? 'rgba(197, 255, 63, 0.4)' : 'var(--line)',
              }}
            >
              {t(`posts.filter_${s}`)}
              {statusFilter !== s && counts[s] != null && counts[s] > 0 && (
                <span style={{ marginLeft: 6, opacity: 0.6, fontFamily: 'var(--mono)', fontSize: 11 }}>
                  {counts[s]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {postsQuery.isLoading && (
        <div style={{ padding: 16, color: 'var(--text-3)' }}>{t('posts.loading')}</div>
      )}
      {!postsQuery.isLoading && posts.length === 0 && (
        <div style={{ padding: 24, color: 'var(--text-3)' }}>{t('posts.empty')}</div>
      )}

      <div className="section">
        <div className="section-body no-pad">
          {posts.map((post) => (
            <PostRow key={post.id} post={post} locale={locale} />
          ))}
        </div>
      </div>
    </>
  );
}

function PostRow({ post, locale }: { post: Post; locale: string }): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stableUrlRef = useRef<string | null>(null);
  if (post.videoUrl && !stableUrlRef.current) {
    stableUrlRef.current = post.videoUrl;
  }
  const videoUrl = stableUrlRef.current;

  function openDetail(e: React.MouseEvent): void {
    // Не перехватываем клики по плееру / линкам — даём им работать.
    const target = e.target as HTMLElement;
    if (target.closest('video, a, button')) return;
    navigate(`/posts/${post.id}`);
  }

  const platformBadge = post.accountPlatform
    ? t(`accounts.platformShort.${post.accountPlatform}`)
    : '?';

  return (
    <div
      onClick={openDetail}
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr 220px',
        gap: 16,
        padding: 16,
        borderBottom: '1px solid var(--line)',
        alignItems: 'flex-start',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: 120,
          aspectRatio: '9 / 16',
          borderRadius: 6,
          background: 'linear-gradient(135deg, #2B5278, #182533)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {videoUrl ? (
          <>
            <video
              src={videoUrl}
              controls
              preload="metadata"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                background: '#000',
              }}
            />
            {post.videoSource === 'source' && (
              <div
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  fontFamily: 'var(--mono)',
                  fontSize: 9,
                  padding: '2px 5px',
                  borderRadius: 3,
                  background: 'rgba(0,0,0,0.7)',
                  color: 'var(--amber, #F59E0B)',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  pointerEvents: 'none',
                }}
                title={t('posts.source_preview_hint')}
              >
                {t('posts.source_preview')}
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.55)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              textAlign: 'center',
              padding: '0 8px',
              lineHeight: 1.4,
            }}
          >
            {t('posts.no_preview')}
          </div>
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            marginBottom: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 10,
              background: `color-mix(in srgb, ${statusColor(post.status)} 18%, transparent)`,
              color: statusColor(post.status),
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 700,
            }}
          >
            {t(`posts.status_${post.status}`)}
          </span>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              color: 'var(--text-3)',
            }}
          >
            {platformBadge} {post.accountHandle ?? ''}
            {post.accountDisplayName ? ` · ${post.accountDisplayName}` : ''}
          </span>
          {post.campaignName && (
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 11,
                color: 'var(--text-3)',
              }}
            >
              · {post.campaignName}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 13,
            color: 'var(--text)',
            marginBottom: 8,
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.caption}
        </div>

        {post.failureReason && (
          <div
            style={{
              padding: '8px 10px',
              background: 'rgba(255,61,110,0.08)',
              border: '1px solid rgba(255,61,110,0.3)',
              borderRadius: 4,
              fontSize: 11,
              color: 'var(--hot)',
              fontFamily: 'var(--mono)',
              marginTop: 8,
            }}
          >
            {post.failureReason}
          </div>
        )}
      </div>

      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 11,
          color: 'var(--text-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          textAlign: 'right',
        }}
      >
        <div>
          <span style={{ opacity: 0.6 }}>{t('posts.scheduledFor')}: </span>
          <span style={{ color: 'var(--text-2)' }}>{fmtDateTime(post.scheduledFor, locale)}</span>
        </div>
        {post.publishedAt && (
          <div>
            <span style={{ opacity: 0.6 }}>{t('posts.publishedAt')}: </span>
            <span style={{ color: 'var(--money)' }}>{fmtDateTime(post.publishedAt, locale)}</span>
          </div>
        )}
        {post.platformPostUrl && (
          <a
            href={post.platformPostUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              marginTop: 8,
              color: 'var(--cool)',
              fontFamily: 'var(--mono)',
              fontSize: 11,
              textDecoration: 'none',
              padding: '4px 8px',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              borderRadius: 4,
              display: 'inline-block',
            }}
          >
            ↗ {t('posts.openInPlatform')}
          </a>
        )}
        {post.viewsCount > 0 && (
          <div style={{ marginTop: 8 }}>
            <span style={{ opacity: 0.6 }}>👁 </span>
            <span style={{ color: 'var(--text-2)' }}>
              {post.viewsCount.toLocaleString(locale)}
            </span>
            {post.likesCount > 0 && (
              <span style={{ marginLeft: 10 }}>
                <span style={{ opacity: 0.6 }}>❤ </span>
                <span style={{ color: 'var(--text-2)' }}>
                  {post.likesCount.toLocaleString(locale)}
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
